import {Role} from "src/app/constant/Enums";
import {StringUtil} from "src/app/util/StringUtil";

/**
 * Parses the session context that MCExecute passes to the Management (Case Audit) page
 * via the iFrame URL query string: `?alias={alias}&role={role}&bu={bu}`.
 *
 * The role is UX-only (it decides which controls/actions are shown). The backend
 * independently enforces authorization from the Midway session — see Case Verifier LLD §9.5.
 */
export interface SessionContext {
    alias: string;
    role: Role;
    bu: string;
}

export namespace SessionParamUtil {

    /**
     * Maps a raw role string (case-insensitive) to a Role enum value.
     * Per HLD §6.4, MC passes one of PRIMARY_AUDITOR / SECONDARY_AUDITOR / QA / MANAGER / UNKNOWN.
     * Falls back to UNKNOWN (no capabilities) when the param is missing or unrecognized —
     * we never default to a privileged role in the UI.
     */
    export function toRole(raw: string | null | undefined): Role {
        if (StringUtil.isNullOrEmpty(raw)) {
            return Role.UNKNOWN;
        }
        const normalized = (raw as string).trim().toUpperCase();
        const match = (Object.values(Role) as string[]).find(r => r === normalized);
        return (match as Role) || Role.UNKNOWN;
    }

    /**
     * Reads the session context from the current window location's query string.
     */
    export function parse(search: string = window.location.search): SessionContext {
        const params = new URLSearchParams(search);
        return {
            alias: params.get('alias') || '',
            role: toRole(params.get('role')),
            bu: params.get('bu') || ''
        };
    }
}
